import React from 'react'
import {
  Navigate,
  Route,
  Routes,
  useParams,
  useLocation,
} from 'react-router-dom'
import { format, startOfMonth } from 'date-fns'
import { DATE_PARAM_FORMAT } from '@/v2/constants'
import { nowOnClinicTimezone } from '@/v2/utils/convert'
import { useAuthContext } from '@/v2/contexts/AuthContext'
import { useUser } from '@/v2/hooks/useUser'
import Calendar from '@/v2/containers/calendar'
import Patient from '@/v2/containers/patient'
import Messages from '@/v2/containers/messages'
import NewConversation from '@/v2/containers/messages/NewConversation'
import useDetectDevice from '@/v2/hooks/useDetectDevice'
import MobileMainRoutes from './MobileMainRoutes.jsx'
import AdminOnly from './AdminOnly.jsx'
import Login from '@/v2/containers/auth-login'
import NewPassword from '@/v2/containers/auth-new-password'
import ResetPassword from '@/v2/containers/auth-reset-password'
import InsuranceForm from '@/v2/containers/insurance-form'
import HealthHistoryForm from '@/v2/containers/health-history-form'
import Tasks from '@/v2/containers/tasks'
import UnconfirmedAppointments from '@/v2/containers/unconfirmed-appointments'
import PerformanceDashboard from '@/v2/containers/performance-dashboard'
import BillingDashboard from '@/v2/containers/billing-dashboard'
import Settings from '@/v2/containers/settings'

// Modal routes typically have these patterns
const MODAL_PATTERNS = [
  '/check-in',
  '/event',
  '/appointment',
  '/time-off',
  '/new-patient',
  '/documents',
  '/finance',
  '/notes',
  '/edit',
  '/basic-info',
  '/contact-info',
  '/emergency-contact',
  '/imaging',
  '/medical-alerts',
  '/insurance',
  '/eligibility',
  '/claim',
  '/task',
  '/crop-image',
  '/consent-form',
  '/create-contract',
  '/contract-details',
]

const PatientRedirect = () => {
  const { id } = useParams()
  return <Navigate to={`/v2/patient/${id}/profile`} replace />
}

const ExternalRedirect = ({ to }) => {
  React.useEffect(() => {
    window.location.href = to
  }, [to])
  return null
}

const CatchAllRoute = () => {
  const location = useLocation()

  // Check if this is a modal route - if so, don't redirect
  const patientTabPattern = /^\/v2\/patient\/\d+\/(profile|treatment|insurance|documents|imaging|finance)(\/progression\/\d+\/series\/\d+)?$/
  const isPatientTab = patientTabPattern.test(location.pathname)
  const isModalPath =
    !isPatientTab &&
    !location.pathname.startsWith('/v2/settings') &&
    MODAL_PATTERNS.some(pattern => {
      // Check if the pattern appears as a path segment (not just a substring)
      // This prevents /v2/tasks from matching /task pattern
      // Allow for additional characters after the pattern (e.g., -confirmation, -details)
      const regex = new RegExp(`${pattern}(/|$|-)`)
      return regex.test(location.pathname)
    })

  if (isModalPath) {
    // This is a modal route, don't redirect - let ModalRoutes handle it
    return null
  }

  // Not a modal route, redirect to 404
  return <ExternalRedirect to="/404" />
}

const MainRoutes = ({ location }) => {
  const { isMobile } = useDetectDevice()
  const { isLoggedIn } = useAuthContext()
  const { user, isDashboardOnly, isLoading: isUserLoading } = useUser({
    enabled: isLoggedIn,
  })
  const isClinicAdmin = user?.roles?.includes('admin') || false

  const rootPath = React.useMemo(
    () =>
      `/v2/calendar/month/${format(
        startOfMonth(Date.now()),
        DATE_PARAM_FORMAT
      )}`,
    []
  )

  if (isMobile) return <MobileMainRoutes location={location} />

  if (isDashboardOnly) {
    return (
      <Routes location={location}>
        <Route path="/v2/dashboard" element={<PerformanceDashboard />} />
        <Route path="*" element={<Navigate to="/v2/dashboard" replace />} />
      </Routes>
    )
  }

  return (
    <Routes location={location}>
      {/* Authentication Routes */}
      {!isLoggedIn && (
        <>
          <Route path="/v2/login" element={<Login />} />
          <Route path="/v2/new-password" element={<NewPassword />} />
          <Route path="/v2/reset-password" element={<ResetPassword />} />
          <Route
            path="/v2/insurance-form/:patientHashId"
            element={<InsuranceForm />}
          />
          <Route
            path="/v2/health-history-form/:uuid"
            element={<HealthHistoryForm mode="fill" />}
          />
        </>
      )}
      {/* End Authentication Routes */}

      {/* Insurance Form Routes */}
      <Route
        path="/v2/insurance-form/:patientHashId"
        element={<InsuranceForm />}
      />
      {/* End Insurance Form Routes */}

      {/* Health History Form Routes */}
      <Route
        path="/v2/health-history-form/:uuid"
        element={<HealthHistoryForm mode="fill" />}
      />
      {/* End Health History Form Routes */}

      {/* Messages Routes */}
      <Route path="/v2/messages/new/:phone" element={<NewConversation />} />
      <Route
        path="/v2/messages/start-conversation/:patientId"
        element={<NewConversation />}
      />
      <Route path="/v2/messages/:conversationId" element={<Messages />} />
      <Route path="/v2/messages" element={<Messages />} />
      {/* End Messages Routes */}

      {/* Calendar Routes */}
      <Route path="/v2/calendar" element={<Navigate to={rootPath} replace />} />
      <Route
        path="/v2/calendar/day"
        element={
          <Navigate
            to={`/v2/calendar/day/${format(
              nowOnClinicTimezone(),
              DATE_PARAM_FORMAT
            )}`}
            replace
          />
        }
      />
      <Route
        path="/v2/calendar/month"
        element={<Navigate to={rootPath} replace />}
      />
      <Route path="/v2/calendar/:view/:date" element={<Calendar />} />
      <Route path="/v2/calendar/:view" element={<Calendar />} />
      {/* End Calendar Routes */}

      {/* Tasks Routes */}
      <Route path="/v2/tasks" element={<Tasks />} />
      {/* End Tasks Routes */}

      {/* Unconfirmed Appointments Routes */}
      <Route
        path="/v2/unconfirmed-appointments"
        element={<UnconfirmedAppointments />}
      />
      {/* End Unconfirmed Appointments Routes */}

      {/* Dashboard Routes */}
      <Route path="/v2/dashboard" element={<PerformanceDashboard />} />
      <Route
        path="/v2/billing-dashboard"
        element={
          <AdminOnly
            isAllowed={isClinicAdmin}
            isResolving={isLoggedIn && isUserLoading}
            redirectTo={rootPath}
          >
            <BillingDashboard />
          </AdminOnly>
        }
      />
      {/* End Dashboard Routes */}

      {/* Settings Routes */}
      <Route
        path="/v2/settings"
        element={<Navigate to="/v2/settings/general" replace />}
      />
      <Route path="/v2/settings/:tab" element={<Settings />} />
      {/* End Settings Routes */}

      {/* Patient Routes */}
      <Route path="/v2/patient/:id" element={<PatientRedirect />} />

      <Route
        path="/v2/patient/:id/:view/progression/:progression/series/:series"
        element={<Patient />}
      />
      <Route
        path="/v2/patient/:id/:view/progression/:progression"
        element={<Patient />}
      />
      <Route path="/v2/patient/:id/:view/*" element={<Patient />} />
      {/* End Patient Routes */}

      {/* Root */}
      <Route path="/v2" element={<Navigate to={rootPath} replace />} />
      {/* End Root */}

      {/* Mobile-only routes - redirect to calendar on desktop */}
      <Route path="/v2/search" element={<Navigate to={rootPath} replace />} />
      {/* End Mobile-only routes */}

      {/* Redirects */}
      <Route path="/settings" element={<ExternalRedirect to="/settings" />} />
      {/* End Redirects */}

      {/* Error Routes */}
      <Route path="/500" element={<ExternalRedirect to="/500" />} />
      <Route path="*" element={<CatchAllRoute />} />
      {/* End Error Routes */}
    </Routes>
  )
}

export default MainRoutes
